import {
  Alert,
  AlertIcon,
  Box,
  Button,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import { eventIsDesignContest } from "../../sourcing-events/buyer/wizard/utils/designContest";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  SourcingEventStatus,
  type EvalBidDto,
  type ReceivedBidDto,
  type SourcingEventDto,
} from "../../../autogen/bff-api";
import { useAwardSelection } from "../award/AwardSelectionContext";
import { juryOnlyAdvises } from "../award/juryRanking";
import { requiresStandstill } from "../award/requiresStandstill";
import { VoluntaryStandstillField } from "../award/VoluntaryStandstillField";
import { StaleDecisionsBanner } from "../evaluation/staleScore";
import { flaggedAmong } from "../designContest/jury";

export const AWARD_CONFIRMATION_STANDSTILL_ID = "award-confirmation-standstill";

const VoluntaryStandstillOutcome = () => {
  const { t } = useTranslation();
  const { standstillDays, standstillProblem } = useAwardSelection();
  if (standstillProblem != null) return null;
  const days = Number(standstillDays.trim());
  return (
    <Text mt="3">
      {days === 0
        ? t("This award has no standstill period.")
        : t(
            "This award has a voluntary standstill period of {{count}} days. Next you approve and send the award letters. The standstill period starts when they are sent.",
            { count: days },
          )}
    </Text>
  );
};

export const AwardConfirmationModal = ({
  event,
  bids,
  evaluationBids,
  onConfirm,
  onClose,
}: {
  event: SourcingEventDto;
  bids: ReceivedBidDto[];
  evaluationBids: EvalBidDto[];
  onConfirm: () => Promise<void>;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { winnerIds, noStandstill, standstillProblem, juryRankingDeparture } = useAwardSelection();
  const [isConfirming, setIsConfirming] = useState(false);
  const winners = bids.filter((bid) => winnerIds.includes(bid.id));
  const voluntary = event.choosesVoluntaryStandstill ?? false;
  const withStatutoryStandstill = !voluntary && requiresStandstill(event) && !noStandstill;
  const contest = eventIsDesignContest(event);
  const oneWinner = winners.length === 1;
  const designContest = event.eventContext.type === "Public" ? event.eventContext.designContest : null;
  const flaggedWinners = winners.filter((bid) => flaggedAmong(designContest, winnerIds).includes(bid.id));

  const confirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={() => !isConfirming && onClose()} size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {!contest
            ? t("Award to the selected suppliers?")
            : oneWinner
              ? t("Choose the winner of the design contest?")
              : t("Choose the winners of the design contest?")}
        </ModalHeader>
        <ModalCloseButton isDisabled={isConfirming} />
        <ModalBody>
          <Text>
            {!contest
              ? t("Do you want to award the contract to:")
              : juryOnlyAdvises(event)
                ? oneWinner
                  ? t("After the jury's assessment, you choose this entry as the winner:")
                  : t("After the jury's assessment, you choose these entries as winners:")
                : oneWinner
                  ? t("The jury's decision names this entry as the winner:")
                  : t("The jury's decision names these entries as winners:")}
          </Text>
          <UnorderedList mt="2" mb={withStatutoryStandstill || voluntary ? "3" : undefined}>
            {winners.map((bid) => (
              <ListItem key={bid.id}>{bid.ownerName}</ListItem>
            ))}
          </UnorderedList>
          {flaggedWinners.length > 0 && (
            <Alert status="warning" rounded="md" fontSize="sm" mb="2">
              <AlertIcon />
              {t(
                "The jury has flagged {{entries}} as identifying. Choosing it as a winner records in the protocol that you went ahead.",
                { entries: flaggedWinners.map((bid) => bid.ownerName).join(", "), count: flaggedWinners.length },
              )}
            </Alert>
          )}
          {juryRankingDeparture === "REFUSED_BY_BINDING_JURY" && (
            <Alert status="warning" rounded="md" fontSize="sm" mb="2">
              <AlertIcon />
              {t("The jury's decision is binding, so the winners must be the entries the jury ranked highest.")}
            </Alert>
          )}
          {contest && <Text>{t("Next you approve and send the letters to the participants. A design contest has no standstill period.")}</Text>}
          {withStatutoryStandstill && (
            <Text>
              {t("Next you approve and send the award letters. The standstill period starts when they are sent.")}
            </Text>
          )}
          {voluntary && (
            <>
              <VoluntaryStandstillField id={AWARD_CONFIRMATION_STANDSTILL_ID} />
              <VoluntaryStandstillOutcome />
            </>
          )}
          <Box mt="3">
            <StaleDecisionsBanner
              bids={evaluationBids}
              evaluationCompleted={event.status === SourcingEventStatus.Evaluated}
            />
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isConfirming}>
            {t("Cancel")}
          </Button>
          <Button
            variant="outline"
            colorScheme="blue"
            onClick={() => void confirm()}
            isLoading={isConfirming}
            isDisabled={(voluntary && standstillProblem != null) || juryRankingDeparture === "REFUSED_BY_BINDING_JURY"}
          >
            {t("Confirm")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
